// hooks/useDirtyRows.ts
import { useMemo, useState, useRef } from "react";

// Your TableView onCellBlur event shape (adjust if needed)
export type CellBlurEvent = {
  rowId: string;
  columnKey: string;
  current: any;
  modified: boolean;
  writePayload?: Record<string, any>; // Enhanced with column write payload
  reset?: () => void; // Reset callback to restore original value
};

export function useDirtyRows<_T>() {
  const [dirtyMap, setDirtyMap] = useState<Record<string, any>>({});
  // Track reset callbacks for each dirty cell
  const resetCallbacksRef = useRef<Map<string, () => void>>(new Map());

  const isDirty = useMemo(() => Object.keys(dirtyMap).length > 0, [dirtyMap]);

  const handleCellBlur = (e: CellBlurEvent) => {
    // Store reset callback if provided
    if (e.reset) {
      const key = `${e.rowId}:${e.columnKey}`;
      if (e.modified) {
        resetCallbacksRef.current.set(key, e.reset);
      } else {
        resetCallbacksRef.current.delete(key);
      }
    }
    setDirtyMap((prev) => {
      const next = { ...prev };
      if (e.modified) {
        // If we have a writePayload, use that instead of simple field update
        if (e.writePayload) {
          // Merge the writePayload structure into the existing data
          const existingRowData = next[e.rowId] || {};

          // Deep merge the writePayload
          const mergedData = { ...existingRowData };
          for (const [key, value] of Object.entries(e.writePayload)) {
            if (typeof value === "object" && value !== null && !Array.isArray(value)) {
              // Merge nested objects (like profiles)
              mergedData[key] = { ...(mergedData[key] || {}), ...value };
            } else {
              mergedData[key] = value;
            }
          }

          next[e.rowId] = mergedData;
        } else {
          // Fallback to legacy behavior
          next[e.rowId] = { ...(next[e.rowId] ?? {}), [e.columnKey]: e.current };
        }
      } else if (next[e.rowId]) {
        // Handle reset/unchanged values
        if (e.writePayload) {
          // Deep unmerge: remove only the specific nested keys from writePayload
          const updated = { ...next[e.rowId] };
          for (const [key, payloadValue] of Object.entries(e.writePayload)) {
            if (
              typeof payloadValue === "object" &&
              payloadValue !== null &&
              !Array.isArray(payloadValue) &&
              typeof updated[key] === "object" &&
              updated[key] !== null
            ) {
              // Deep unmerge: remove nested keys
              const nestedUpdated = { ...updated[key] };
              for (const nestedKey of Object.keys(payloadValue)) {
                delete nestedUpdated[nestedKey];
              }
              if (Object.keys(nestedUpdated).length === 0) {
                delete updated[key];
              } else {
                updated[key] = nestedUpdated;
              }
            } else {
              delete updated[key];
            }
          }

          if (Object.keys(updated).length === 0) {
            const { [e.rowId]: __, ...restRows } = next;
            return restRows;
          }
          next[e.rowId] = updated;
        } else {
          // Legacy behavior
          const { [e.columnKey]: _, ...rest } = next[e.rowId]!;
          if (Object.keys(rest).length === 0) {
            const { [e.rowId]: __, ...restRows } = next;
            return restRows;
          }
          next[e.rowId] = rest;
        }
      }
      return next;
    });
  };

  const discardAll = () => {
    // Call all reset callbacks to restore original values
    resetCallbacksRef.current.forEach((reset) => reset());
    resetCallbacksRef.current.clear();
    setDirtyMap({});
  };

  return { dirtyMap, setDirtyMap, isDirty, handleCellBlur, discardAll };
}
